import classNames from 'classnames';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import SubmitArrowButtonIcon from '@/components/Button/SubmitArrowButtonIcon';
import { PasswordInputField } from '@/components/InputFields';
import Button from '@/shared/components/Button';
import ErrorMessage from '@/shared/components/ErrorMessage';

import HiddenIdentifierInput from './HiddenIdentifierInput';
import styles from './index.module.scss';

type Props = {
  readonly className?: string;
  // eslint-disable-next-line react/boolean-prop-naming
  readonly autoFocus?: boolean;
  readonly onSubmit: (password: string) => Promise<void>;
  readonly errorMessage?: string;
  readonly clearErrorMessage?: () => void;
  readonly appearance?: 'plain';
};

type FieldState = {
  newPassword: string;
};

const Lite = ({
  className,
  autoFocus,
  onSubmit,
  errorMessage,
  clearErrorMessage,
  appearance,
}: Props) => {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FieldState>({
    reValidateMode: 'onBlur',
    defaultValues: { newPassword: '' },
  });

  useEffect(() => {
    if (!isValid) {
      clearErrorMessage?.();
    }
  }, [clearErrorMessage, isValid]);

  const onSubmitHandler = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      clearErrorMessage?.();

      await handleSubmit(async (data) => {
        await onSubmit(data.newPassword);
      })(event);
    },
    [clearErrorMessage, handleSubmit, onSubmit]
  );

  return (
    <form
      className={classNames(styles.form, appearance === 'plain' && styles.plain, className)}
      onSubmit={onSubmitHandler}
    >
      <HiddenIdentifierInput />
      <PasswordInputField
        className={styles.inputField}
        appearance={appearance}
        autoComplete="new-password"
        label={t('input.password')}
        {...(appearance === 'plain' ? { placeholder: t('description.enter_password') } : {})}
        autoFocus={autoFocus}
        isDanger={!!errors.newPassword}
        errorMessage={errors.newPassword?.message}
        aria-invalid={!!errors.newPassword}
        {...register('newPassword', {
          required: t('error.password_required'),
        })}
      />

      {errorMessage && <ErrorMessage className={styles.formErrors}>{errorMessage}</ErrorMessage>}

      <Button
        name="submit"
        title="action.save_password"
        htmlType="submit"
        icon={appearance === 'plain' ? <SubmitArrowButtonIcon /> : undefined}
        iconPlacement={appearance === 'plain' ? 'edge' : 'inline'}
        isLoading={isSubmitting}
      />

      <input hidden type="submit" />
    </form>
  );
};

export default Lite;
